import React, { useState, useMemo } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
} from '@wove/web-ui'
import { useCustomFields } from './useCustomFields'
import {
  RFQ_FIELD_OPTIONS,
  SHIPMENT_FIELD_OPTIONS,
  REQUIREMENT_ONLY_RFQ_FIELDS,
  REQUIREMENT_ONLY_SHIPMENT_FIELDS,
  SHIPMENT_CONTAINER_FIELD_OPTIONS,
} from './fieldOptions'

export interface ValidationRequirement {
  fieldPath: string
  requirementType: 'required' | 'optional' | 'hidden' | 'disabled'
  errorMessage?: string
  context: 'base' | 'shipment' | 'both' | 'container'
}

interface RequirementsEditorProps {
  requirements: ValidationRequirement[]
  onChange: (requirements: ValidationRequirement[]) => void
}

export function RequirementsEditor({ requirements, onChange }: RequirementsEditorProps) {
  const { data: customFields = [] } = useCustomFields()
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [formData, setFormData] = useState<ValidationRequirement>({
    fieldPath: '',
    requirementType: 'required',
    errorMessage: '',
    context: 'shipment',
  })

  // Build field options including custom fields and requirement-only fields
  const allFields = useMemo(() => {
    const standardFields = [
      ...RFQ_FIELD_OPTIONS.map((f) => ({ ...f, context: 'base' as const })),
      ...REQUIREMENT_ONLY_RFQ_FIELDS.map((f) => ({ ...f, context: 'base' as const })),
      ...SHIPMENT_FIELD_OPTIONS.map((f) => ({ ...f, context: 'shipment' as const })),
      ...SHIPMENT_CONTAINER_FIELD_OPTIONS.map((f) => ({
        ...f,
        context: 'container' as const,
      })),
      ...REQUIREMENT_ONLY_SHIPMENT_FIELDS.map((f) => ({ ...f, context: 'shipment' as const })),
    ]

    // Add custom fields
    const customFieldOptions = customFields.map((field) => ({
      value: `customFields.${field.field_key}`,
      label: field.field_name,
      context:
        field.applies_to === 'rfq'
          ? ('base' as const)
          : field.applies_to === 'container'
          ? ('container' as const)
          : ('shipment' as const),
      category: 'custom',
      fieldType: field.field_type,
    }))

    return [...standardFields, ...customFieldOptions]
  }, [customFields])

  const handleAdd = () => {
    if (!formData.fieldPath) return

    if (editingIndex !== null) {
      const newRequirements = [...requirements]
      newRequirements[editingIndex] = formData
      onChange(newRequirements)
      setEditingIndex(null)
    } else {
      onChange([...requirements, formData])
    }

    setFormData({
      fieldPath: '',
      requirementType: 'required',
      errorMessage: '',
      context: 'shipment',
    })
  }

  const handleEdit = (index: number) => {
    setFormData(requirements[index])
    setEditingIndex(index)
  }

  const handleRemove = (index: number) => {
    onChange(requirements.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Field</label>
            <Select
              value={formData.fieldPath}
              onValueChange={(value) => {
                const field = allFields.find((f) => f.value === value)
                setFormData({
                  ...formData,
                  fieldPath: value,
                  context: (field?.context as any) || 'shipment',
                })
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select field" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_divider_rfq" disabled>
                  <span className="font-semibold">RFQ Fields</span>
                </SelectItem>
                {RFQ_FIELD_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
                {REQUIREMENT_ONLY_RFQ_FIELDS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                    <span className="text-xs text-gray-500 ml-2">(requirement only)</span>
                  </SelectItem>
                ))}
                {customFields
                  .filter((f) => f.applies_to === 'rfq')
                  .map((field) => (
                    <SelectItem key={field.field_key} value={`customFields.${field.field_key}`}>
                      {field.field_name}
                      <span className="text-xs text-gray-500 ml-2">
                        (custom, {field.field_type})
                      </span>
                    </SelectItem>
                  ))}

                <SelectItem value="_divider_shipment" disabled>
                  <span className="font-semibold">Shipment Fields</span>
                </SelectItem>
                {SHIPMENT_FIELD_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
                {REQUIREMENT_ONLY_SHIPMENT_FIELDS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                    <span className="text-xs text-gray-500 ml-2">(requirement only)</span>
                  </SelectItem>
                ))}
                {customFields
                  .filter((f) => f.applies_to === 'shipment')
                  .map((field) => (
                    <SelectItem key={field.field_key} value={`customFields.${field.field_key}`}>
                      {field.field_name}
                      <span className="text-xs text-gray-500 ml-2">
                        (custom, {field.field_type})
                      </span>
                    </SelectItem>
                  ))}
                <SelectItem value="_divider_shipment_container" disabled>
                  <span className="font-semibold">Shipment Container Fields</span>
                </SelectItem>
                {SHIPMENT_CONTAINER_FIELD_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
                {customFields
                  .filter((f) => f.applies_to === 'container')
                  .map((field) => (
                    <SelectItem key={field.field_key} value={`customFields.${field.field_key}`}>
                      {field.field_name}
                      <span className="text-xs text-gray-500 ml-2">
                        (custom, {field.field_type})
                      </span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Requirement Type</label>
            <Select
              value={formData.requirementType}
              onValueChange={(value) => {
                const newFormData = { ...formData, requirementType: value as any }
                // Clear error message when switching to hidden
                if (value === 'hidden') {
                  newFormData.errorMessage = ''
                }
                setFormData(newFormData)
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="required">Required</SelectItem>
                <SelectItem value="optional">Optional</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {formData.requirementType !== 'hidden' && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {formData.requirementType === 'disabled' ? 'Info Message' : 'Error Message'}{' '}
              (optional)
            </label>
            <Textarea
              value={formData.errorMessage || ''}
              onChange={(e) => setFormData({ ...formData, errorMessage: e.target.value })}
              placeholder={
                formData.requirementType === 'disabled'
                  ? 'Info message to display when field is disabled'
                  : 'Custom error message when field is required but empty'
              }
              rows={2}
            />
          </div>
        )}

        <div className="flex justify-end gap-2">
          {editingIndex !== null && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditingIndex(null)
                setFormData({
                  fieldPath: '',
                  requirementType: 'required',
                  errorMessage: '',
                  context: 'shipment',
                })
              }}
            >
              Cancel
            </Button>
          )}
          <Button type="button" onClick={handleAdd} disabled={!formData.fieldPath}>
            <Plus className="h-4 w-4 mr-2" />
            {editingIndex !== null ? 'Update' : 'Add'} Requirement
          </Button>
        </div>
      </div>

      {requirements.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Field</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Context</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requirements.map((req, index) => {
              // Find the field label for display
              const field = allFields.find((f) => f.value === req.fieldPath)
              const displayName = field?.label || req.fieldPath
              const isCustomField = req.fieldPath.startsWith('customFields.')

              return (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    {displayName}
                    {isCustomField && <span className="text-xs text-gray-500 ml-2">(custom)</span>}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        req.requirementType === 'required'
                          ? 'destructive'
                          : req.requirementType === 'optional'
                          ? 'secondary'
                          : req.requirementType === 'disabled'
                          ? 'default'
                          : 'outline'
                      }
                    >
                      {req.requirementType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{req.context}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{req.errorMessage || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(index)}
                        className="h-8 w-8"
                      >
                        <span className="sr-only">Edit</span>
                        ✏️
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemove(index)}
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
